import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export default function SalesFrameworks() {
  const canonical = 'https://earnmyedge.com/sales-frameworks';
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Sales Framework Guides',
    url: canonical,
    about: [
      { '@type': 'Thing', name: 'SPIN Selling' },
      { '@type': 'Thing', name: 'Sandler' },
      { '@type': 'Thing', name: 'Challenger' },
      { '@type': 'Thing', name: 'MEDDIC' },
      { '@type': 'Thing', name: 'NEPQ' },
      { '@type': 'Thing', name: 'GAP Selling' },
      { '@type': 'Thing', name: 'Straight Line' },
      { '@type': 'Thing', name: 'Value Selling' },
    ],
  };

  return (
    <main className="mx-auto max-w-4xl px-6 py-12">
      <Helmet>
        <title>Sales Framework Guides | Earn My Edge</title>
        <meta name="description" content="Learn top sales frameworks with guided lessons and practice: SPIN, Sandler, Challenger, MEDDIC, NEPQ, GAP, Straight Line, and Value Selling." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Sales Framework Guides | Earn My Edge" />
        <meta property="og:description" content="Learn top sales frameworks with guided lessons and practice: SPIN, Sandler, Challenger, MEDDIC, NEPQ, GAP, Straight Line, and Value Selling." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="/assets/social-card.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sales Framework Guides | Earn My Edge" />
        <meta name="twitter:description" content="Learn top sales frameworks with guided lessons and practice: SPIN, Sandler, Challenger, MEDDIC, NEPQ, GAP, Straight Line, and Value Selling." />
        <meta name="twitter:image" content="/assets/social-card.png" />
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Sales Framework Guides</h1>
        <p className="mt-4 text-muted-foreground">
          Compare approaches, learn core patterns, and practice scenarios tailored to each methodology.
        </p>
      </header>

      <section className="grid gap-6 sm:grid-cols-2">
        {[
          'SPIN Selling',
          'Sandler',
          'Challenger',
          'MEDDIC',
          'NEPQ',
          'GAP Selling',
          'Straight Line',
          'Value Selling',
        ].map((name) => (
          <div key={name} className="rounded-lg border p-4">
            <h3 className="font-medium">{name}</h3>
            <p className="mt-2 text-sm text-muted-foreground">Overview, common patterns, and practice scenarios.</p>
            <div className="mt-3">
              <Link to="/app/mega-trainer" className="text-primary underline">Open Mega Trainer</Link>
            </div>
          </div>
        ))}
      </section>

      <section className="mt-12 pt-6 border-t">
        <h2 className="text-2xl font-medium mb-6">Framework Questions</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-medium">Which framework should I learn first?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Start with SPIN or Sandler if you're new. They provide strong foundations in questioning and qualification.</p>
          </div>
          <div>
            <h3 className="font-medium">Can I mix techniques from different frameworks?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Yes. Many top performers blend frameworks. Start with one, master its core, then integrate techniques from others.</p>
          </div>
          <div>
            <h3 className="font-medium">How long does it take to master a framework?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Expect 4-6 weeks of consistent practice to internalize core patterns and apply them naturally in conversations.</p>
          </div>
        </div>
      </section>

      <footer className="mt-10 border-t pt-6">
        <p className="font-medium">Related pages</p>
        <ul className="mt-2 list-disc pl-6">
          <li><Link to="/sales-training" className="underline">Sales Training</Link></li>
          <li><Link to="/ai-sales-coach" className="underline">AI Sales Coach</Link></li>
          <li><Link to="/objection-handling-training" className="underline">Objection Handling Training</Link></li>
          <li><Link to="/sales-roleplay-scenarios" className="underline">Sales Roleplay Scenarios</Link></li>
        </ul>
      </footer>
    </main>
  );
}
